<template>
  <div class="mt30">
    <div class="events-list-page-content">
      <div class="left-column">
        <h2 class="title mt5">
          {{ pageTitle }}
        </h2>
        <div><slot name="description" /></div>
        <slot name="share-buttons" />
        <local-map v-if="agraConfiguration.mapTilesProvider"
                   class="local-map mt20"
                   :prop-filters="propFilters"
                   :organisation-boundaries="organisationBoundaries"
                   :marker-redirect="true" />
        <div class="mt20">
          <slot name="stats" />
        </div>
      </div>
      <div class="right-column mt5">
        <filterable-events-list ref="local-list"
                                class="local-list"
                                :prop-filters="propFilters"
                                :allowed-advanced-filters="allowedAdvancedFilters"
                                :empty-list-component="emptyListComponent"
                                @paginate="onPaginate" />
        <div v-if="showCreateEventButtonAfterResults" class="mt20 under-list">
          <create-new-event-button class="create-button" size="md" text-i18n-key="calendars.show.new_event" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmptyEventListWide from './empty-event-list-wide'
import FilterableEventsList from './filterable-events-list'
import LocalMap from './local-map'
import CreateNewEventButton from './create-new-event-button'
import { mapGetters, mapState } from 'vuex'
import { filterManagerMixin } from './filter-manager'
import { sharedMapPropsMixin } from './shared-map-props'
import { sharedLocalListPropsMixin } from './shared-local-list-props'
import { FILTER_KEY_CALENDARS, FILTER_KEY_TYPES } from './filter-keys'
import { URL_KEY_PARTNER } from './url-keys'

export default {
  components: { CreateNewEventButton, FilterableEventsList, LocalMap },

  mixins: [filterManagerMixin, sharedLocalListPropsMixin, sharedMapPropsMixin],

  props: {
    // Consumers of this component may customize the filter `calendars`
    // without including them in the URL of the page.
    calendarsFilter: {
      type: Array,
      required: false,
      default: () => []
    },
    emptyListComponent: {
      type: Object,
      default: () => EmptyEventListWide
    },
    filterOptions: {
      type: Object,
      required: true
    },
    pageTitle: {
      type: String,
      required: true
    },
    allowedAdvancedFilters: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  computed: {
    ...mapState(['detailedLocations']),
    ...mapGetters(['showNewEventButton']),

    propFilters() {
      return [
        {
          type: FILTER_KEY_CALENDARS,
          value: this.calendarsFilter
        },
        {
          type: FILTER_KEY_TYPES,
          value: ['event']
        }
      ]
    },

    showCreateEventButtonAfterResults() {
      if (!this.detailedLocations) {
        // Events have not loaded yet
        return false
      } else if (this.detailedLocations.length === 0) {
        // There are no events, so we're potentially showing a "create your own" prompt in the empty list.
        // Don't duplicate it.
        return false
      } else {
        return this.showNewEventButton
      }
    }
  },

  created() {
    const additionalUrlKeysIncludedInItemPageUrls = [ URL_KEY_PARTNER ]
    this.$store.commit('setAdditionalUrlKeysIncludedInItemPageUrls', additionalUrlKeysIncludedInItemPageUrls)
    this.$store.commit('setUrlFilterKeys', this.urlFilterKeys)
    this.$store.commit('setOptionsFromUrlAndFilters', this.propFiltersNotForUrl)
    this.$store.commit('setFilterOptions', this.filterOptions)
    this.$store.dispatch('getPoints')
    this.$store.dispatch('loadDataFromUrl')
  },

  methods: {
    onPaginate() {
      this.$refs['local-list'].$el.scrollIntoView()
    }
  }
}
</script>

<style lang="scss" scoped>
  @use "~common_styles/breakpoints" as breakpoints;

  .events-list-page-content {
    display: flex;

    @include breakpoints.screen-mobile-only {
      flex-direction: column;
    }
  }

  .left-column {
    flex-basis: 200px;

    @include breakpoints.screen-mobile-only {
      margin-top: 10px;
    }
  }

  @include breakpoints.screen-mobile-only {
    .title {
      display: flex;
      justify-content: center;
    }
  }

  .right-column {
    flex: 1 0;

    @include breakpoints.screen-md-up {
      margin-left: 20px;
    }

    @include breakpoints.screen-mobile-only {
      margin-top: 20px;
    }
  }

  .local-map {
    height: 400px;
    width: 100%;
    display: block;
  }

  .under-list {
    display: flex;
    justify-content: flex-end
  }

</style>
